import { useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { useActor } from '@/auth/pin/phoneSession'
import { newId, newUlid } from '@/lib/ids'
import { istDateKey } from '@/lib/ist'
import { sumPaise } from '@/lib/money'
import { getMediaBlob } from '@/offline/media'
import type { RosterFarmer } from '@/offline'
import { useSyncStatus } from '@/offline'
import { useToast } from '@/ui'
import '../commands'
import { useSupervisorPin } from '../components/SupervisorPin'
import { commitDelivery, mintLot, saveDeliveryDraft, voidDelivery } from '../data/actions'
import type { GateRoster } from '../data/hooks'
import type { GateSession } from '../data/session'
import { useGate } from '../data/useGate'
import {
  deliveryReducer,
  draftPayloads,
  furthestStep,
  hasMainLot,
  lotPlan,
  lotsWithFigures,
  newDraft,
  type DeliveryDraft,
  type StepId,
  type V1TaskId,
} from '../model/delivery'
import { pickCard } from '../model/rateCards'
import { usePersistentDraft, WizardContext, type WizardCtx } from '../pages/delivery/context'
import type { PaperSlipHeader } from '../pages/delivery/DeliveryPage'
import { CropScreen, DivertScreen, StorageScreen } from './delivery/Details'
import { DoneScreen, FinalScreen, type FixTarget } from './delivery/Final'
import { FindFarmerV1 } from './delivery/Find'
import { MoistureScreen } from './delivery/Moisture'
import { SealScreen } from './delivery/Seal'
import { V1Context, type V1Ctx } from './delivery/ctx'
import { FarmerSlipScreen, PaperSlipScreen } from './delivery/Slip'
import { TagScanScreen, WeighScreen } from './delivery/Tag'
import { V1Screen, V1Sheet } from './kit'
import { C } from './style'
import { tr } from './text'
import { BankCollectScreen } from './bank/BankCollect'
import { StepHeader } from './delivery/shared'
import { TasksScreen } from './delivery/Tasks'
import { useQueuedBanks } from '../data/hooks'

type Screen =
  | 'crop'
  /** Live days: the task list after the crop (any order), then the slip. */
  | 'tasks'
  | 'storage'
  | 'divert'
  | 'moisture'
  | 'tagScan'
  | 'weigh'
  | 'seal'
  /** V1: his bank account, asked just before the slip when he has none. */
  | 'bank'
  | 'farmerSlip'
  | 'paperSlip'
  | 'final'

const STEP_OF: Record<Screen, StepId> = {
  crop: 'details',
  tasks: 'details',
  storage: 'details',
  divert: 'details',
  moisture: 'moisture',
  tagScan: 'tag',
  weigh: 'tag',
  seal: 'sample',
  bank: 'slip',
  farmerSlip: 'slip',
  paperSlip: 'slip',
  final: 'close',
}

interface Done {
  kind: 'COMMITTED' | 'VOIDED'
  farmer: { name: string; roster_no: string }
  bags: number
  netG: number
  totalPaise: number
  parts: number
}

/**
 * Seva Mandir V1 delivery (F4.2 → F4.10) on the alignment map's V1 boards: find the farmer,
 * crop and two questions, moisture (groundnut), tag → weigh per bag, seal, his slip, the paper
 * slip, final check, closed. Paper back-entry types a slip through the same screens.
 *
 * Several farmers can be open at once on the phone: `?id=<delivery_ulid>` opens one of them
 * where she left it, and no id finds a new farmer. Paper back-entry keeps one slip at a time.
 */
export function V1DeliveryPage({
  paperSlip,
  onPaperDone,
}: {
  paperSlip?: PaperSlipHeader
  onPaperDone?: () => void
}) {
  const navigate = useNavigate()
  const actor = useActor()
  const toast = useToast()
  const g = useGate()
  const sync = useSyncStatus()
  const queuedBanks = useQueuedBanks()
  const [params] = useSearchParams()
  const [starting, setStarting] = useState(false)
  const [done, setDone] = useState<Done | null>(null)
  /** The farmer just started here, until the drafts list catches up with him. */
  const [started, setStarted] = useState<string | null>(null)
  const paper = !!paperSlip || !!onPaperDone
  const session = paper ? g.paper : g.app
  const mode = paper ? 'PAPER_BACKFILL' : 'APP'
  const openId = paper ? null : params.get('id')
  const open = g.drafts.filter(
    (x) => x.data.capture_mode === mode && session && x.data.session_id === session.id,
  )
  const draftRec = paper ? open[0] : open.find((x) => x.data.delivery_ulid === openId)
  const home = () => (paper && onPaperDone ? onPaperDone() : navigate('/m/gate'))
  const openFarmer = (ulid: string) =>
    navigate(`/m/gate/delivery/new?id=${encodeURIComponent(ulid)}`, { replace: true })
  // Tags and seals on the other open farmers: never scanned onto this one.
  const otherCodes = new Map<string, string>()
  for (const x of open) {
    if (x.data.delivery_ulid === draftRec?.data.delivery_ulid) continue
    for (const code of draftPayloads(x.data)) otherCodes.set(code, x.data.farmer.name)
  }

  if (!g.ready || !g.roster) return <V1Screen />
  if (done) {
    return (
      <DoneScreen
        {...done}
        onNext={() => {
          if (paper) {
            setDone(null)
            home()
          } else if (open.length > 0) {
            // Others still open: back to the list to pick who is ready.
            navigate('/m/gate')
          } else {
            navigate('/m/gate/delivery/new', { replace: true })
            setDone(null)
          }
        }}
      />
    )
  }
  if (!session) return <Navigate to={paper ? '/m/gate/paper' : '/m/gate'} replace />

  if (!draftRec && openId) {
    // Just started: the list has not caught up yet. Otherwise he was closed or deleted.
    return openId === started ? <V1Screen /> : <Navigate to="/m/gate" replace />
  }

  if (!draftRec) {
    const start = async (f: RosterFarmer, identification: DeliveryDraft['identification']) => {
      // Already open on this phone: carry on with him, never a second copy.
      const same = open.find((x) => x.data.farmer.id === f.user_id)
      if (same && !paper) {
        toast.success(tr('gate.v1.home.alreadyOpen', { name: f.name }))
        openFarmer(same.data.delivery_ulid)
        return
      }
      setStarting(true)
      try {
        const at = paperSlip?.slip_time ?? new Date().toISOString()
        const lot = await mintLot(at)
        const d = newDraft({
          delivery_ulid: newUlid(),
          session_id: session.id,
          capture_mode: mode,
          now: at,
          scope: 'V1',
          farmer: {
            id: f.user_id,
            name: f.name,
            roster_no: f.roster_no,
            father_name: f.father_name ?? null,
            phone_last4: f.phone_last4 ?? null,
            photo_url: f.photo_url ?? null,
            bank_verified: f.bank?.verified ?? false,
            account_last4: f.bank?.account_last4 ?? null,
            // Being approved at the office, or typed in offline and waiting on this phone.
            bank_pending_last4: f.bank?.pending_last4 ?? queuedBanks.get(f.user_id) ?? null,
            bank_queued: !f.bank?.pending_last4 && queuedBanks.has(f.user_id),
          },
          identification,
          main_lot: lot,
          paper: paperSlip ? { ...paperSlip, receipt_slip_id: newId() } : undefined,
        })
        if (paperSlip) {
          d.slip.slip_no = paperSlip.slip_no
          d.slip.photo_media_id = paperSlip.page_photo_media_id
        }
        await saveDeliveryDraft(d, actor.user_id)
        if (!paper) {
          setStarted(d.delivery_ulid)
          openFarmer(d.delivery_ulid)
        }
      } catch (e) {
        toast.error(tr('gate.v1.saveFailed'), e instanceof Error ? e.message : String(e))
      } finally {
        setStarting(false)
      }
    }
    return (
      <>
        <FindFarmerV1
          farmers={g.roster.farmers}
          village={session.village_name}
          pending={sync.pending}
          onBack={home}
          onConfirm={(f, id) => void start(f, id)}
          busy={starting}
        />
      </>
    )
  }

  return (
    <V1Wizard
      key={draftRec.data.delivery_ulid}
      initial={draftRec.data}
      session={session}
      roster={g.roster}
      outbox={g.outbox}
      used={g.used}
      otherCodes={otherCodes}
      pending={sync.pending}
      onDone={setDone}
      onExit={home}
    />
  )
}

/**
 * Where an open farmer resumes. Live days: the task list (or the slip once he agreed it). Paper
 * back-entry: the furthest step reached, not one she went back to look at.
 */
function firstScreen(d: DeliveryDraft): Screen {
  if (d.capture_mode === 'APP') {
    if (d.step === 'close') return 'final'
    if (d.slip.farmer_agreed_at) return 'paperSlip'
    if (d.step === 'slip') return 'farmerSlip'
    return d.crop_id ? 'tasks' : 'crop'
  }
  switch (furthestStep(d)) {
    case 'details':
      return d.crop_id ? 'storage' : 'crop'
    case 'moisture':
      return 'moisture'
    case 'tag':
    case 'check':
    case 'price':
      return 'tagScan'
    case 'sample':
      return 'seal'
    case 'slip':
      return d.slip.farmer_agreed_at ? 'paperSlip' : 'farmerSlip'
    case 'close':
      return 'final'
  }
}

function V1Wizard({
  initial,
  session,
  roster,
  outbox,
  used,
  otherCodes,
  pending,
  onDone,
  onExit,
}: {
  initial: DeliveryDraft
  session: GateSession
  roster: GateRoster
  outbox: WizardCtx['outbox']
  used: WizardCtx['used']
  otherCodes: ReadonlyMap<string, string>
  pending: number
  onDone: (d: Done) => void
  onExit: () => void
}) {
  const toast = useToast()
  const actor = useActor()
  const sup = useSupervisorPin()
  const { draft: d, dispatch, flush } = usePersistentDraft(initial, actor.user_id)
  const [screen, setScreen] = useState<Screen>(() => firstScreen(initial))
  const [scanned, setScanned] = useState<{ payload: string; entry: 'SCAN' | 'TYPED' } | null>(null)
  const [busy, setBusy] = useState(false)
  const [page, setPage] = useState(false)
  const paper = d.capture_mode === 'PAPER_BACKFILL'

  const dateKey = istDateKey(d.paper?.slip_time ?? d.started_at)
  const crop = roster.config.crops.find((c) => c.crop_id === d.crop_id)
  const cards = useMemo<WizardCtx['cards']>(() => {
    const out: WizardCtx['cards'] = { MAIN: null, CONV: null, problem: null }
    if (!d.crop_id) return out
    for (const s of ['MAIN', 'CONV'] as const) {
      const p = pickCard(roster.rateCards, d.crop_id, s, dateKey)
      if (p.ok) out[s] = p.card
      else if (!out.problem && lotPlan(d).some((l) => l.stream === s))
        out.problem = { stream: s, kind: p.problem }
    }
    return out
  }, [d, roster.rateCards, dateKey])

  const ref = (s: 'MAIN' | 'CONV') => {
    const c = cards[s]
    return c && c.price_paise_per_kg != null
      ? { id: c.id, price_paise_per_kg: c.price_paise_per_kg }
      : undefined
  }

  const go = (s: Screen) => {
    setScreen(s)
    if (STEP_OF[s] !== d.step) dispatch({ type: 'goto', step: STEP_OF[s] })
  }

  const summary = (kind: Done['kind'], dd: DeliveryDraft): Done => {
    const lots = lotsWithFigures(dd, { MAIN: ref('MAIN'), CONV: ref('CONV') })
    return {
      kind,
      farmer: { name: dd.farmer.name, roster_no: dd.farmer.roster_no },
      bags: lots.reduce((s, l) => s + l.plan.bag_count, 0),
      netG: lots.reduce((s, l) => s + l.figures.net_g, 0),
      totalPaise: sumPaise(lots.map((l) => l.figures.amount_paise)),
      parts: lots.length,
    }
  }

  const ctx: V1Ctx = {
    d,
    dispatch,
    session,
    roster,
    outbox,
    used,
    otherCodes,
    actor,
    crop,
    cards,
    dateKey,
    stamp: () => d.paper?.slip_time ?? new Date().toISOString(),
    requestSupervisor: sup.request,
    startDispute: () => undefined,
    pending,
    exit: onExit,
    busy,
    commit: async () => {
      setBusy(true)
      try {
        await flush()
        await commitDelivery(d, session, actor.user_id, {
          cards: { MAIN: ref('MAIN'), CONV: ref('CONV') },
          season_light: 'GREEN',
        })
        onDone(summary('COMMITTED', d))
      } catch (e) {
        toast.error(tr('gate.v1.saveFailed'), e instanceof Error ? e.message : String(e))
      } finally {
        setBusy(false)
      }
    },
    cancel: async (reason) => {
      setBusy(true)
      try {
        const action = {
          type: 'start_cancel' as const,
          reason,
          reason_code:
            reason === 'DIVERTED_TO_DRY'
              ? (roster.config.divert_reasons[0]?.code ?? 'LOOKS_WET')
              : null,
          at: ctx.stamp(),
        }
        const next = deliveryReducer(d, action)
        dispatch(action)
        await flush()
        await voidDelivery(next, session, actor.user_id)
        onDone(summary('VOIDED', next))
      } catch (e) {
        toast.error(tr('gate.v1.saveFailed'), e instanceof Error ? e.message : String(e))
      } finally {
        setBusy(false)
      }
    },
  }

  // Live days: every task opens from the list and comes back to it. Paper back-entry copies a
  // finished slip top to bottom, so it keeps the steps in their order.
  const toTasks = () => go('tasks')
  const afterStorage = () => {
    if (!d.crop_id) return
    if (!paper) return toTasks()
    go(d.moisture_required ? 'moisture' : 'tagScan')
  }
  const afterTags = () => (paper ? go(hasMainLot(d) ? 'seal' : 'final') : toTasks())
  const afterSeal = () => (paper ? go('final') : toTasks())
  const beforeSeal = (): Screen => (paper ? 'tagScan' : 'tasks')
  const beforeTags = (): Screen => (!paper ? 'tasks' : d.moisture_required ? 'moisture' : 'storage')
  const beforeSlip = (): Screen => (!paper ? 'tasks' : hasMainLot(d) ? 'seal' : 'tagScan')
  const TASK_SCREEN: Record<V1TaskId, Screen> = {
    forms: 'storage',
    moisture: 'moisture',
    bags: 'tagScan',
    seal: 'seal',
    bank: 'bank',
  }
  const fix = (t: FixTarget) =>
    go(
      (
        {
          storage: 'storage',
          crop: 'crop',
          moisture: 'moisture',
          tag: 'tagScan',
          seal: 'seal',
          farmerSlip: 'farmerSlip',
          paperSlip: 'paperSlip',
        } as const
      )[t],
    )

  let body
  switch (screen) {
    case 'crop':
      body = (
        <CropScreen
          onBack={onExit}
          onNext={() => {
            if (!d.crop_id) return
            go(paper ? 'storage' : 'tasks')
          }}
        />
      )
      break
    case 'tasks':
      body = (
        <TasksScreen
          onBack={() => go('crop')}
          onCrop={() => go('crop')}
          onOpen={(t) => go(TASK_SCREEN[t])}
          onSlip={() => go(d.slip.farmer_agreed_at ? 'paperSlip' : 'farmerSlip')}
        />
      )
      break
    case 'storage':
      body = (
        <StorageScreen
          onBack={() => go(paper ? 'crop' : 'tasks')}
          onNext={afterStorage}
          onDry={() => setScreen('divert')}
        />
      )
      break
    case 'divert':
      body = (
        <DivertScreen
          onBack={() => setScreen('storage')}
          onConfirm={() => void ctx.cancel('DIVERTED_TO_DRY')}
        />
      )
      break
    case 'moisture':
      body = (
        <MoistureScreen
          onBack={() => go(paper ? 'storage' : 'tasks')}
          onNext={() => go(paper ? 'tagScan' : 'tasks')}
        />
      )
      break
    case 'tagScan':
      body = (
        <TagScanScreen
          onBack={() => go(beforeTags())}
          onScanned={(s) => {
            setScanned(s)
            setScreen('weigh')
          }}
          onDone={afterTags}
        />
      )
      break
    case 'weigh':
      body = scanned ? (
        <WeighScreen
          scanned={scanned}
          onBack={() => {
            setScanned(null)
            setScreen('tagScan')
          }}
          onSaved={() => {
            setScanned(null)
            setScreen('tagScan')
          }}
          onSeePage={paper ? () => setPage(true) : undefined}
        />
      ) : (
        <TagScanScreen
          onBack={() => go(beforeTags())}
          onScanned={(s) => setScanned(s)}
          onDone={afterTags}
        />
      )
      break
    case 'seal':
      body = <SealScreen onBack={() => go(beforeSeal())} onNext={afterSeal} />
      break
    case 'bank':
      body = (
        <BankCollectScreen
          farmer={{ id: d.farmer.id, name: d.farmer.name }}
          header={
            <StepHeader
              step="slip"
              icon="passbook"
              title={tr('gate.v1.bank.title')}
              onBack={() => go(beforeSlip())}
            />
          }
          onDone={(a) => {
            dispatch({ type: 'bank_added', last4: a.last4, queued: a.queued })
            go(paper ? 'farmerSlip' : 'tasks')
          }}
          onSkip={() => {
            dispatch({ type: 'bank_later' })
            go(paper ? 'farmerSlip' : 'tasks')
          }}
        />
      )
      break
    case 'farmerSlip':
      body = <FarmerSlipScreen onBack={() => go(beforeSlip())} onAgreed={() => go('paperSlip')} />
      break
    case 'paperSlip':
      body = <PaperSlipScreen onBack={() => go('farmerSlip')} onNext={() => go('final')} />
      break
    case 'final':
      body = <FinalScreen onBack={() => go(paper ? beforeSlip() : 'paperSlip')} onFix={fix} />
      break
  }

  return (
    <WizardContext.Provider value={ctx}>
      <V1Context.Provider value={ctx}>
        {body}
        {page && d.paper?.page_photo_media_id && (
          <PagePhotoSheet mediaId={d.paper.page_photo_media_id} onClose={() => setPage(false)} />
        )}
        {sup.sheet}
      </V1Context.Provider>
    </WizardContext.Provider>
  )
}

/** पन्ना देखें: the paper page photo, big. */
function PagePhotoSheet({ mediaId, onClose }: { mediaId: string; onClose: () => void }) {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    let u: string | null = null
    let alive = true
    void getMediaBlob(mediaId).then((b) => {
      if (!alive || !b) return
      u = URL.createObjectURL(b)
      setUrl(u)
    })
    return () => {
      alive = false
      if (u) URL.revokeObjectURL(u)
    }
  }, [mediaId])
  return (
    <V1Sheet title={tr('gate.v1.paper.pagePhoto')} onClose={onClose} top={40}>
      <div
        style={{
          flexGrow: 1,
          borderRadius: 12,
          background: C.photo,
          overflow: 'hidden',
          minHeight: 300,
        }}
      >
        {url && (
          <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        )}
      </div>
    </V1Sheet>
  )
}
